import React, { ReactNode } from 'react';
import {
  SellerConnectScreeningMode,
  SellerConnectIntakeStatus,
} from 'models/SellerConnectEngine';
import {
  LOWEST_HIGHEST_COPY,
  OLDEST_NEWEST_COPY,
  SortingDirectionValues,
} from 'models/Sort';
import { getFormattedDate } from 'dateUtils';
import IntakeViaChip from '../../IntakeViaChip';
import IntakeStatusTag from '../../IntakeStatusTag';
import { EMPTY_CELL_VALUE } from 'data/global';

export type SellersTableData = {
  _id: string;
  submissionId: string;
  seller: string;
  business: string;
  industry: string;
  location: string;
  revenueRange: string;
  intakeVia: SellerConnectScreeningMode | null;
  intakeStatus: SellerConnectIntakeStatus | null;
  submittedAt: string | null;
};

export type SellersTableColumns = Array<keyof SellersTableData>;

export const COLUMNS: SellersTableColumns = [
  'seller',
  'business',
  'industry',
  'location',
  'revenueRange',
  'intakeVia',
  'intakeStatus',
  'submittedAt',
];

export const HEADING_TRANSLATIONS: Partial<
  Record<typeof COLUMNS[number], string>
> = {
  seller: 'seller',
  business: 'business',
  industry: 'industry',
  location: 'location',
  revenueRange: 'annualRevenue',
  intakeVia: 'intakeVia',
  intakeStatus: 'imStatus',
  submittedAt: 'date',
};

export const SORTING_VALUES: Partial<Record<typeof COLUMNS[number], string>> = {
  seller: 'fullName',
  business: 'businessName',
  industry: 'industry',
  location: 'state',
  revenueRange: 'revenueRange',
  intakeVia: 'mode',
  intakeStatus: 'intakeStatus',
  submittedAt: 'submittedAt',
};

export const MULTI_SORT_DIRECTION_COPY: Record<
  string,
  SortingDirectionValues[string]
> = {
  [SORTING_VALUES.revenueRange]: LOWEST_HIGHEST_COPY,
  [SORTING_VALUES.submittedAt]: OLDEST_NEWEST_COPY,
};

export const DEFAULT_SORT = '-submittedAt';

export const LOCKED_COLUMN: keyof SellersTableData = 'seller';

export const CUSTOM_COL_SIZES: Partial<Record<typeof COLUMNS[number], number>> =
  {
    seller: 200,
    business: 220,
    industry: 200,
    location: 120,
    revenueRange: 140,
    intakeVia: 120,
    intakeStatus: 160,
    submittedAt: 120,
  };

type CellProps = {
  data: SellersTableData;
};

const IntakeViaCell: React.FC<CellProps> = ({ data }) => {
  const { intakeVia } = data;

  if (!intakeVia) return <>{EMPTY_CELL_VALUE}</>;

  return <IntakeViaChip mode={intakeVia} />;
};

const IntakeStatusCell: React.FC<CellProps> = ({ data }) => {
  const { intakeStatus } = data;

  if (!intakeStatus) return <>{EMPTY_CELL_VALUE}</>;

  return <IntakeStatusTag status={intakeStatus} />;
};

const SubmittedAtCell: React.FC<CellProps> = ({ data }) => {
  const { submittedAt } = data;

  if (!submittedAt) return <>{EMPTY_CELL_VALUE}</>;

  return <>{getFormattedDate(new Date(submittedAt))}</>;
};

export const BODY_CONTENT_GENERATE: Record<
  keyof SellersTableData,
  (cellProps: CellProps) => ReactNode
> = {
  _id: null,
  submissionId: null,
  seller: ({ data }) => data.seller,
  business: ({ data }) => data.business,
  industry: ({ data }) => data.industry,
  location: ({ data }) => data.location,
  revenueRange: ({ data }) => data.revenueRange,
  intakeVia: (cellProps) => <IntakeViaCell {...cellProps} />,
  intakeStatus: (cellProps) => <IntakeStatusCell {...cellProps} />,
  submittedAt: (cellProps) => <SubmittedAtCell {...cellProps} />,
};
